/**
 * Where this deployment's identity provider lives, learned at runtime.
 *
 * The client bundle is the same artefact in every environment, so it cannot be built with
 * an issuer compiled into it. It asks the API for the public parameters of the flow, then
 * reads the provider's own discovery document for the endpoints. Nothing about the
 * provider is hard-coded here, which is what keeps a change of provider a configuration
 * change (ADR-022 section 7).
 */

export interface AuthConfig {
  readonly issuer: string;
  readonly clientId: string;
  readonly scope: string;
}

export interface OidcEndpoints {
  readonly authorizationEndpoint: string;
  readonly tokenEndpoint: string;
  readonly endSessionEndpoint: string;
}

function asRecord(value: unknown): Record<string, unknown> {
  if (typeof value !== "object" || value === null) {
    throw new Error("The response was not an object");
  }
  return value as Record<string, unknown>;
}

function requireString(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`The response is missing ${key}`);
  }
  return value;
}

async function getJson(url: string): Promise<Record<string, unknown>> {
  const response = await fetch(url, { headers: { Accept: "application/json" } });
  if (!response.ok) {
    throw new Error(`Request to ${url} failed with ${String(response.status)}`);
  }
  return asRecord(await response.json());
}

/** The public OIDC parameters of this deployment, from our own API. */
export async function loadAuthConfig(): Promise<AuthConfig> {
  const body = await getJson("/api/auth/config");
  return {
    issuer: requireString(body, "issuer"),
    clientId: requireString(body, "client_id"),
    scope: requireString(body, "scope"),
  };
}

/** The provider's endpoints, from its own discovery document rather than from a guess. */
export async function discoverEndpoints(issuer: string): Promise<OidcEndpoints> {
  const body = await getJson(`${issuer.replace(/\/$/, "")}/.well-known/openid-configuration`);
  return {
    authorizationEndpoint: requireString(body, "authorization_endpoint"),
    tokenEndpoint: requireString(body, "token_endpoint"),
    endSessionEndpoint: requireString(body, "end_session_endpoint"),
  };
}
